"use client";

import * as amplitude from "@amplitude/analytics-browser";
import type { PortalUser } from "@/contexts/AuthContext";

let started = false;
let activePartnerCode: string | null = null;

export function setAnalyticsActivePartner(code: string | null): void {
  activePartnerCode = code;
}

export function startAnalytics(): void {
  if (started) {
    return;
  }
  const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;
  const appVersion = process.env.NEXT_PUBLIC_APP_VERSION;
  if (!apiKey) {
    console.warn("[analytics] NEXT_PUBLIC_AMPLITUDE_API_KEY not set — analytics disabled");
    return;
  }
  try {
    amplitude.init(apiKey, {
      appVersion,
      autocapture: {
        attribution: true,
        pageViews: true,
        sessions: true,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: false,
      },
    });
    amplitude.add({
      name: "active-partner",
      type: "enrichment",
      execute: async (event) => {
        if (activePartnerCode) {
          event.event_properties = {
            ...event.event_properties,
            active_partner_code: activePartnerCode,
          };
        }
        return event;
      },
    });
    started = true;
  } catch (err) {
    console.warn("[analytics] initialisation failed", err);
  }
}

function trackEvent(event: string, props?: Record<string, unknown>): void {
  if (process.env.NODE_ENV === "development") {
    console.log("[analytics]", event, props);
  }
  if (!started) {
    return;
  }
  try {
    amplitude.track(event, props);
  } catch (err) {
    console.warn("[analytics] track failed", err);
  }
}

export function setAnalyticsUser(user: PortalUser | null): void {
  if (!started) {
    return;
  }
  try {
    if (!user) {
      amplitude.reset();
      return;
    }
    amplitude.setUserId(user.userId);
    const identify = new amplitude.Identify();
    identify.set("email", user.email);
    identify.set("internalRole", user.internalRole ?? "");
    identify.set("partnerCodes", user.grants?.map((g) => g.partnerCode) ?? []);
    identify.set(
      "partnerRoles",
      user.grants?.map((g) => `${g.partnerCode}:${g.partnerRole}`) ?? []
    );
    amplitude.identify(identify);
  } catch (err) {
    console.warn("[analytics] identify failed", err);
  }
}

export { trackEvent };
